/**
 * <wishlist-page> — docs/35 F3. Step 10.4.
 *
 * Reads the same storage `component-product-card.js` writes, and re-renders
 * whenever it changes. See the D4 note in sections/main-wishlist.liquid for
 * why that storage is localStorage.
 *
 * Cards come from the Section Rendering API, not from JS — docs/47 §18.5.
 */
(function () {
  if (customElements.get('wishlist-page')) return;

  const KEY = 'dramour:wishlist';

  function read() {
    try {
      const parsed = JSON.parse(window.localStorage.getItem(KEY) || '[]');
      return Array.isArray(parsed) ? parsed.filter((h) => typeof h === 'string' && h) : [];
    } catch {
      return [];
    }
  }

  class WishlistPage extends HTMLElement {
    connectedCallback() {
      if (this.dataset.bound === 'true') return;
      this.dataset.bound = 'true';

      this.grid = this.querySelector('.wishlist__grid');
      this.empty = this.querySelector('[data-wishlist-empty]');
      this.status = this.querySelector('[data-wishlist-status]');

      this.render();

      /**
       * The card's heart is on this page too — removing an item has to take
       * its card away rather than leave a dead tile. `wishlist:change` is
       * the event component-product-card.js already dispatches, so nothing
       * new is needed on that side.
       */
      document.addEventListener('wishlist:change', () => this.render());
    }

    async render() {
      const handles = read();

      if (handles.length === 0) {
        this.grid.hidden = true;
        this.grid.replaceChildren();
        this.empty.hidden = false;
        this.announce(0);
        return;
      }

      const query = handles.map((handle) => `handle:${handle}`).join(' OR ');
      const url = `${window.Shopify?.routes?.root || '/'}search?section_id=${encodeURIComponent(
        this.dataset.sectionId,
      )}&type=product&q=${encodeURIComponent(query)}`;

      try {
        const response = await fetch(url);
        if (!response.ok) throw new Error(`Wishlist: ${response.status}`);
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const source = doc.querySelector('.wishlist__grid');
        const items = source ? [...source.children] : [];

        if (items.length === 0) {
          // Saved handles that no longer resolve — a deleted or unpublished
          // product. The empty state is the honest answer, not a blank grid.
          this.grid.hidden = true;
          this.grid.replaceChildren();
          this.empty.hidden = false;
          this.announce(0);
          return;
        }

        this.grid.replaceChildren(...items.map((item) => document.importNode(item, true)));
        this.grid.hidden = false;
        this.empty.hidden = true;
        this.announce(items.length);
      } catch (error) {
        console.error(error);
        // Leave whatever is on screen rather than replacing a populated grid
        // with an empty state because one fetch failed.
      }
    }

    announce(count) {
      if (!this.status) return;
      // `[count]`, not `{{ count }}` — a Liquid output tag inside a Liquid
      // string literal is a syntax error, so the section passes Dawn's
      // placeholder convention instead. See the note in main-wishlist.liquid.
      const template = count === 1 ? this.dataset.labelOne : this.dataset.labelOther;
      this.status.textContent = template ? template.replace('[count]', count) : String(count);
    }
  }

  customElements.define('wishlist-page', WishlistPage);
})();
